import { Editable, EnqueteEditor } from "@bleu/nbsr-editor";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { FunctionComponent, useCallback, useState } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";

import { ServerEndpoint } from "../../../../../../server/router/http/endpoint";
import i18n from "../../../../lib/i18n";
import { uploadImage, uploadVideo } from "../../../../lib/queries";
import { upsertJob } from "../../../../lib/queries/job";
import { Job } from "../../../../types";
import { Attention } from "../../../parts/Attention";

type Props = {
	job: Editable<Job>;
};

export const EnqueteEditorPage: FunctionComponent<Props> = React.memo(({ job, ...props }) => {
	const [t] = useTranslation();

	const queryClient = useQueryClient();

	const [_job, setJob] = useState(job);

	const { mutateAsync: mutateJob } = useMutation({
		mutationFn: async (job: Editable<Job>) =>
			upsertJob(job).then((res) => {
				const { state, value } = res.payload;
				if (state === "loaded") {
					queryClient.setQueryData(["job", job._id], value);
					setJob(value);
				}
				return res;
			}),
	});

	const handleSave = useCallback(
		async (job: Editable<Job>) => {
			return await mutateJob(job);
		},
		[mutateJob]
	);

	const { mutateAsync: handleUploadImage } = useMutation({
		mutationFn: async (file: File) => {
			if (job._id == null)
				return { error: { message: t("enqueteEditorPage.jobIdIsUndefined") }, state: "failed" as const };

			return await uploadImage(`${ServerEndpoint.internal.image.base}/enquete/${job._id}`, file);
		},
	});

	const { mutateAsync: handleUploadVideo } = useMutation({
		mutationFn: uploadVideo,
	});

	return (
		<div className="enquete-editor-page">
			<EnqueteEditor
				job={_job}
				variants={{
					controlSecondary: "outline-secondary",
				}}
				webEndpoints={{
					debug: ServerEndpoint.internal.redirect.enqueteDebugBase,
					enquete: ServerEndpoint.internal.redirect.enqueteDebugBase,
					preview: ServerEndpoint.internal.redirect.enquetePreviewBase,
				}}
				onSave={handleSave}
				onUpdate={setJob}
				onUploadImage={handleUploadImage}
				onUploadVideo={handleUploadVideo}
				{...props}
			>
				<I18nextProvider i18n={i18n}>
					<Attention />
				</I18nextProvider>
			</EnqueteEditor>
		</div>
	);
});
